/**
 * BUGFIX (Nova Análise) — busca manual, sem disparo automático.
 *
 * Abrir `/search` não consulta o backend: a pesquisa só roda no clique do usuário.
 * Aqui ficam o timeout de UX e a normalização do resultado, com I/O injetado para
 * permitir teste sem rede. O cache-first e o provedor continuam intocados.
 */

/** Teto de espera da interface para uma busca manual. */
export const SEARCH_TIMEOUT_MS = 20_000;

export const SEARCH_TIMEOUT_MESSAGE = "A busca está demorando mais que o esperado. Tente novamente.";
export const SEARCH_ERROR_MESSAGE = "Não foi possível buscar partidas. Tente novamente.";

export interface SearchRunResult<T> {
  value: T | null;
  error: string | null;
  timedOut: boolean;
}

/**
 * Executa a busca com timeout e cancelamento.
 *
 * O `signal` é repassado ao cliente HTTP (o fetch é abortado de fato quando
 * possível) e a espera do frontend é encerrada no teto — o backend segue seus
 * próprios jobs normalmente, sem retry silencioso.
 */
export async function runSearchWithTimeout<T>(
  task: (signal: AbortSignal) => Promise<{ value: T | null; error: string | null }>,
  options: { timeoutMs?: number } = {}
): Promise<SearchRunResult<T>> {
  const timeoutMs = options.timeoutMs ?? SEARCH_TIMEOUT_MS;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<SearchRunResult<T>>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve({ value: null, error: SEARCH_TIMEOUT_MESSAGE, timedOut: true });
    }, timeoutMs);
  });

  const work = (async (): Promise<SearchRunResult<T>> => {
    try {
      const result = await task(controller.signal);
      if (result.error) return { value: result.value, error: result.error, timedOut: false };
      return { value: result.value, error: null, timedOut: false };
    } catch (error) {
      // Abortar por timeout já produz a mensagem no `timeout`; aqui tratamos falhas reais.
      return {
        value: null,
        error: error instanceof Error && error.message ? error.message : SEARCH_ERROR_MESSAGE,
        timedOut: false,
      };
    }
  })();

  try {
    return await Promise.race([work, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
